/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/alt-text */
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Button,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import WriteLogo from "../../assets/images/write.svg";
import DeleteLogo from "../../assets/images/delete.svg";
import {
  addList,
  getList,
  getListById,
  postRecipe,
  updateById,
} from "../../services/services";
import { ENDPOINT } from "../../services/api-endpoint";
import AppLoader from "../../components/AppLoader";
import { usePageLoader } from "../../services/usePageLoader";
import { useConfirmDialog } from "../../services/useConfirmDialog";
import MesEmptyState from "../../components/mes-empty-state/mes-empty-state";
import RecipeGradeMultiSelect from "../../components/recipe-grade-multi-select/recipe-grade-multi-select";
import { useMaterialCatalog } from "../../hooks/useMaterialCatalog";
import {
  applyDefaultCellsFromGroups,
  buildDisplayWorkflowOps,
  resolveRecipeGroupsForStep,
  validateRecipeTnmOrdering,
  type RecipeGroupDef,
} from "../../lib/recipe-groups";
import { fetchRecipeProcessParameterGroups } from "../../lib/recipe-process-parameters";
import "./mes-workflows.scss";
import {
  buildWorkflowOrderKey,
  findPartOptionValue,
  fetchPartsForWorkflowOrder,
  fetchWorkflowOrderOptions,
  parseWorkflowOrderKey,
  type WorkflowOrderOption,
  type WorkflowPartOption,
} from "./workflow-order-helpers";
import type { OrderSource } from "../mes-orders/mes-order-utils";

type WorkflowRow = {
  id: number;
  workflowname?: string | null;
  template_name?: string | null;
  partid?: string | null;
  order_part_name?: string | null;
  orderPartName?: string | null;
  order_line_id?: string | null;
  orderLineId?: string | null;
  orderid?: string | null;
  order_source?: string | null;
  orderSource?: string | null;
  partDisplayName?: string | null;
  part_name?: string | null;
  order_code?: string | null;
  orderCode?: string | null;
  status?: string | null;
  recipeCount?: number;
  recipe_count?: number;
  step_count?: number;
  stepCount?: number;
  total_duration_minutes?: number;
  totalDurationMinutes?: number;
  active?: boolean;
  workflowoperations?: WoRow[];
};

type WoRow = {
  id?: number;
  opterionid?: string | null;
  cycletime?: string | null;
  setuptime?: string | null;
  active?: boolean;
  processDefinitionName?: string | null;
  machineName?: string | null;
  mfgTechnologyName?: string | null;
  mfgTechnologyId?: number | null;
  workcenterId?: number | null;
  synthetic?: boolean;
};

type StepRow = {
  id?: number;
  opterionid: string;
  cycletime: string;
  setuptime: string;
};

type OperationOpt = {
  id: number;
  operationname?: string | null;
  workcentername?: string | null;
  machineTypeName?: string | null;
  mfgTechnologyName?: string | null;
  machineTypeId?: number | null;
};

function parseMinutes(v: string | null | undefined): number {
  const s = (v ?? "").trim();
  if (!s) return 0;
  if (s.includes(":")) {
    const parts = s.split(":");
    const h = parseInt(parts[0] ?? "0", 10) || 0;
    const m = parseInt(parts[1] ?? "0", 10) || 0;
    return h * 60 + m;
  }
  const n = parseInt(s, 10);
  return Number.isNaN(n) ? 0 : n;
}

function workflowTotals(w: WorkflowRow): { steps: number; minutes: number } {
  const ops = (w.workflowoperations ?? []).filter((o) => o.active !== false);
  let minutes = 0;
  for (const o of ops) {
    minutes += parseMinutes(o.cycletime) + parseMinutes(o.setuptime);
  }
  return { steps: ops.length, minutes };
}

function workflowListMeta(w: WorkflowRow): { steps: number; minutes: number } {
  const sc = w.step_count ?? w.stepCount;
  const td = w.total_duration_minutes ?? w.totalDurationMinutes;
  if (typeof sc === "number" && typeof td === "number") {
    return { steps: sc, minutes: td };
  }
  return workflowTotals(w);
}

function normalizeWorkflowPayload(w: unknown): WorkflowRow | null {
  if (!w || typeof w !== "object") return null;
  const o = w as Record<string, unknown>;
  const id = Number(o.id);
  if (!Number.isFinite(id)) return null;
  return w as WorkflowRow;
}

const MesWorkflows = () => {
  const [validated, setValidated] = useState(false);
  const [formInputs, setForm] = useState<Record<string, string>>({});
  const [editId, setEditId] = useState<number | "">("");
  const [partsList, setParts] = useState<WorkflowPartOption[]>([]);
  const [ordersList, setOrdersList] = useState<WorkflowOrderOption[]>([]);
  const ordersListRef = useRef<WorkflowOrderOption[]>([]);
  const [orderPartsLoading, setOrderPartsLoading] = useState(false);
  const [workflowsCollection, setWorkflows] = useState<WorkflowRow[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [pageSize] = useState<number>(10);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>("");
  const [openAddUpdateModal, setAddUpdateModal] = useState<boolean>(false);
  const [operationList, setOperation] = useState<OperationOpt[]>([]);
  const { isLoading, runWithLoading } = usePageLoader();
  const { confirm, ConfirmDialog } = useConfirmDialog();

  const [expandedWorkflowId, setExpandedWorkflowId] = useState<number | null>(null);
  const [workflowDetails, setWorkflowDetails] = useState<Record<number, WorkflowRow>>({});
  const workflowDetailsRef = useRef<Record<number, WorkflowRow>>({});

  const [steps, setSteps] = useState<StepRow[]>([]);
  const [draftOpId, setDraftOpId] = useState("");
  const [draftCycle, setDraftCycle] = useState(10);
  const [draftSetup, setDraftSetup] = useState(5);
  const [saveLoading, setSaveLoading] = useState(false);
  const [recipePanelWfId, setRecipePanelWfId] = useState<number | null>(null);
  const [recipeName, setRecipeName] = useState("");
  const [recipeCategoryId, setRecipeCategoryId] = useState("");
  const [recipeGradeIds, setRecipeGradeIds] = useState<number[]>([]);
  const { categories: materialCategories, grades: materialGrades } =
    useMaterialCatalog(recipeCategoryId);
  const [recipeSavedId, setRecipeSavedId] = useState<number | null>(null);
  const [recipeSaveBusy, setRecipeSaveBusy] = useState(false);
  const [recipeCells, setRecipeCells] = useState<Record<string, string>>({});
  const [stepParamGroupsByWf, setStepParamGroupsByWf] = useState<
    Record<number, RecipeGroupDef[][]>
  >({});
  const [recipeValidated, setRecipeValidated] = useState(false);
  /** `basic` = name/category/grades; `full` = also required parameter targets (approve). */
  const [recipeValidationTier, setRecipeValidationTier] = useState<"idle" | "basic" | "full">(
    "idle",
  );

  const searchDebounceRef = useRef<number>();

  useEffect(() => {
    ordersListRef.current = ordersList;
  }, [ordersList]);

  useEffect(() => {
    workflowDetailsRef.current = workflowDetails;
  }, [workflowDetails]);

  useEffect(() => {
    setRecipeGradeIds([]);
  }, [recipeCategoryId]);

  useEffect(() => {
    setRecipeGradeIds((prev) =>
      prev.filter((id) => materialGrades.some((g) => g.id === id)),
    );
  }, [materialGrades]);

  useEffect(() => {
    setRecipePanelWfId(null);
  }, [expandedWorkflowId]);

  useEffect(() => {
    setRecipeValidated(false);
    setRecipeValidationTier("idle");
    if (recipePanelWfId == null) {
      setRecipeName("");
      setRecipeCategoryId("");
      setRecipeGradeIds([]);
      setRecipeCells({});
      setRecipeSavedId(null);
      return;
    }
    setRecipeSavedId(null);
    setRecipeCategoryId("");
    setRecipeGradeIds([]);
    setRecipeCells({});
    const wf = workflowsCollection.find((w) => w.id === recipePanelWfId);
    const base =
      (wf?.template_name ?? wf?.workflowname ?? "").trim() || "Workflow";
    setRecipeName(`${base} — Recipe`);
  }, [recipePanelWfId, workflowsCollection]);

  useEffect(() => {
    if (recipePanelWfId == null) return;
    const detail = workflowDetails[recipePanelWfId];
    if (!detail || stepParamGroupsByWf[recipePanelWfId]) return;
    let cancelled = false;
    void (async () => {
      try {
        const groups = await fetchRecipeProcessParameterGroups(recipePanelWfId);
        if (cancelled) return;
        setStepParamGroupsByWf((prev) => ({ ...prev, [recipePanelWfId]: groups }));
        const detailOps = (detail.workflowoperations ?? []).filter((o) => o.active !== false);
        const displayOps = buildDisplayWorkflowOps(detailOps as WoRow[]);
        let nextCells: Record<string, string> = {};
        displayOps.forEach((op, sIdx) => {
          const stepGroups = resolveRecipeGroupsForStep(groups, sIdx, op);
          nextCells = applyDefaultCellsFromGroups(nextCells, sIdx, stepGroups);
        });
        setRecipeCells(nextCells);
      } catch (e) {
        console.log(`Error loading recipe process parameters: ${e}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [recipePanelWfId, workflowDetails, stepParamGroupsByWf]);

  useEffect(() => {
    const id = expandedWorkflowId;
    if (!id) return;
    let cancelled = false;
    if (workflowDetailsRef.current[id]) return;
    void (async () => {
      try {
        const raw = await getListById(ENDPOINT.WORKFLOWS_LIST_BY_ID, id);
        if (cancelled) return;
        const wfRaw = Array.isArray(raw) ? raw[0] : raw;
        const wf = normalizeWorkflowPayload(wfRaw);
        if (wf) {
          workflowDetailsRef.current = { ...workflowDetailsRef.current, [id]: wf };
          setWorkflowDetails({ ...workflowDetailsRef.current });
        }
      } catch (e) {
        console.log(`Error loading workflow detail: ${e}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [expandedWorkflowId]);

  const opNameById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const o of operationList) {
      m[String(o.id)] = (o.operationname ?? "").trim() || `Operation #${o.id}`;
    }
    return m;
  }, [operationList]);

  const opMetaById = useMemo(() => {
    const m: Record<
      string,
      { machine: string; tech: string; techId: number | null }
    > = {};
    for (const o of operationList) {
      const tech =
        (o.mfgTechnologyName || o.machineTypeName || "").trim() || "—";
      const machine = (o.workcentername || "").trim() || "—";
      const techId =
        o.machineTypeId != null && Number.isFinite(Number(o.machineTypeId))
          ? Number(o.machineTypeId)
          : null;
      m[String(o.id)] = { machine, tech, techId };
    }
    return m;
  }, [operationList]);

  const handleClose = () => {
    setEditId("");
    setForm({});
    setParts([]);
    setSteps([]);
    setDraftOpId("");
    setDraftCycle(10);
    setDraftSetup(5);
    setAddUpdateModal(false);
    setValidated(false);
    setSaveLoading(false);
  };

  const loadPartsForOrder = useCallback(
    async (orderKey: string, restore?: { wf?: WorkflowRow; preserveValue?: string }) => {
      const parsed = parseWorkflowOrderKey(orderKey);
      if (!parsed) {
        setParts([]);
        return;
      }
      setOrderPartsLoading(true);
      try {
        const filtered = await fetchPartsForWorkflowOrder(parsed.source, parsed.id);
        setParts(filtered);
        if (filtered.length === 0) {
          toast.info("No parts found for this order.");
        }
        const preserve =
          restore?.preserveValue ??
          (restore?.wf ? findPartOptionValue(filtered, restore.wf) : "");
        if (preserve && filtered.some((p) => p.value === preserve)) {
          setForm((values) => ({ ...values, partid: preserve }));
        } else if (filtered.length === 1) {
          setForm((values) => ({ ...values, partid: filtered[0].value }));
        } else {
          setForm((values) => ({ ...values, partid: "" }));
        }
      } catch (e) {
        console.log(`Error loading parts for order: ${e}`);
        setParts([]);
      } finally {
        setOrderPartsLoading(false);
      }
    },
    [],
  );

  const loadModalLists = useCallback(async () => {
    try {
      const [ops, orders] = await Promise.all([
        getList(ENDPOINT.OPERATIONS_LIST),
        fetchWorkflowOrderOptions(),
      ]);
      setOperation(Array.isArray(ops) ? ops : []);
      setOrdersList(orders);
    } catch (e) {
      console.log(`Error loading modal lists: ${e}`);
    }
  }, []);

  const onLoad = useCallback(
    async (nextPageIndex: number = 0, overrideSearchText?: string) => {
      try {
        await runWithLoading(async () => {
          const effectiveSearchText =
            overrideSearchText !== undefined ? overrideSearchText : searchText;

          const request = {
            pagesize: pageSize,
            pagenumber: nextPageIndex,
            serachtext: effectiveSearchText,
          };

          const [resp, orderOptions] = await Promise.all([
            getList(ENDPOINT.WORKFLOWS_LIST, request),
            fetchWorkflowOrderOptions().catch(() => [] as WorkflowOrderOption[]),
          ]);
          const items = Array.isArray(resp) ? resp : (resp as any)?.data || [];
          const count = Array.isArray(resp)
            ? items.length
            : Number((resp as any)?.count || 0);
          const orderCodeByKey = new Map(
            orderOptions.map((o) => [o.orderKey, o.ordercode]),
          );

          setWorkflows(
            (Array.isArray(items) ? items : []).map((r) => {
              const row = r as WorkflowRow;
              const src = (row.order_source ?? row.orderSource ?? "manual") as OrderSource;
              const oid = row.orderid != null ? Number(row.orderid) : NaN;
              const orderKey =
                Number.isFinite(oid) && oid > 0 ? buildWorkflowOrderKey(src, oid) : "";
              const resolvedCode = orderKey ? orderCodeByKey.get(orderKey) : undefined;
              return {
                ...row,
                order_code: resolvedCode ?? row.order_code ?? row.orderCode,
                orderCode: resolvedCode ?? row.orderCode ?? row.order_code,
                status:
                  row.status && String(row.status).trim() ? String(row.status) : "Draft",
              };
            }),
          );
          setTotalCount(count);
          setPageIndex(nextPageIndex);
          setExpandedWorkflowId(null);
          workflowDetailsRef.current = {};
          setWorkflowDetails({});
        });
      } catch (e) {
        console.log(`Error in getList workflows: ${e}`);
      }
    },
    [searchText, pageSize, runWithLoading],
  );

  useEffect(() => {
    void onLoad(0, "");
    // Initial list only; search uses scheduleSearchReload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const list = await getList(ENDPOINT.OPERATIONS_LIST);
        setOperation(Array.isArray(list) ? list : []);
      } catch (e) {
        console.log(`Error prefetching operations: ${e}`);
      }
    })();
  }, []);

  const scheduleSearchReload = useCallback(
    (value: string) => {
      window.clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = window.setTimeout(() => {
        void onLoad(0, value);
      }, 320);
    },
    [onLoad],
  );

  const saveWorkflowRecipe = useCallback(
    async (workflowId: number, status: "draft" | "approved") => {
      const detail = workflowDetails[workflowId];
      const detailOps = (detail?.workflowoperations ?? [])
        .filter((o) => o.active !== false)
        .sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
      const displayOps = buildDisplayWorkflowOps(detailOps as WoRow[]);
      if (!displayOps.length) {
        toast.error("No workflow steps to save. Wait for steps to load, then try again.");
        return;
      }

      setRecipeValidated(true);
      setRecipeValidationTier(status === "approved" ? "full" : "basic");
      const name = recipeName.trim();
      const cat = Number(recipeCategoryId);
      const valErrors: string[] = [];
      if (!name) valErrors.push("Recipe name is required.");
      if (!Number.isFinite(cat) || cat <= 0) valErrors.push("Select a material category.");
      if (recipeGradeIds.length === 0) valErrors.push("Select at least one material grade.");
      const stepGroups = stepParamGroupsByWf[workflowId] ?? null;
      if (status === "approved") {
        for (let sIdx = 0; sIdx < displayOps.length; sIdx++) {
          const op = displayOps[sIdx]!;
          const groups = resolveRecipeGroupsForStep(stepGroups, sIdx, op);
          for (const g of groups) {
            for (const p of g.params) {
              if (!p.required) continue;
              const k = `${sIdx}-${p.key}-t`;
              if (!(recipeCells[k] ?? "").trim()) {
                valErrors.push(
                  `"${p.label}" in step ${sIdx + 1} needs a target value before approval.`,
                );
              }
            }
          }
        }
      }
      valErrors.push(...validateRecipeTnmOrdering(recipeCells));
      if (valErrors.length) {
        const shown = valErrors.slice(0, 2).join(" ");
        const more =
          valErrors.length > 2 ? ` (+${valErrors.length - 2} more)` : "";
        toast.error(`${shown}${more}`);
        return;
      }

      const workflowOperationIds = displayOps
        .map((o) => Number(o.id))
        .filter((n) => Number.isFinite(n) && n > 0);
      const firstGrade =
        recipeGradeIds.find((id) => materialGrades.some((g) => g.id === id)) ?? null;
      setRecipeSaveBusy(true);
      try {
        const payload = { cells: { ...recipeCells }, workflowOperationIds };
        const baseReq = {
          workflowId,
          name,
          materialCategoryId: Number.isFinite(cat) && cat > 0 ? cat : null,
          materialGradeId: firstGrade,
          materialGradeIds: recipeGradeIds.length ? recipeGradeIds : undefined,
          payload,
        };
        let nextId = recipeSavedId;
        if (!nextId) {
          /** First save is always draft; approve only on a later save when recipe id exists. */
          const created = await postRecipe({
            ...baseReq,
            status: "draft",
          });
          nextId = created.id;
        } else {
          await postRecipe({
            ...baseReq,
            id: nextId,
            status,
          });
        }
        setRecipeSavedId(nextId ?? null);
        setRecipeValidated(false);
        setRecipeValidationTier("idle");
        toast.success(
          !recipeSavedId
            ? "Recipe saved as draft."
            : status === "approved"
              ? "Recipe saved as approved."
              : "Recipe saved as draft.",
        );
        await onLoad(pageIndex, searchText);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Could not save recipe.");
      } finally {
        setRecipeSaveBusy(false);
      }
    },
    [
      recipeName,
      workflowDetails,
      recipeCategoryId,
      recipeGradeIds,
      materialGrades,
      stepParamGroupsByWf,
      recipeCells,
      recipeSavedId,
      onLoad,
      pageIndex,
      searchText,
    ],
  );

  const handleShowCreate = () => {
    setValidated(false);
    setEditId("");
    setForm({ workflowname: "", orderKey: "", partid: "", status: "Draft" });
    setParts([]);
    setSteps([]);
    setDraftOpId("");
    setDraftCycle(10);
    setDraftSetup(5);
    void loadModalLists();
    setAddUpdateModal(true);
  };

  const handleSubmit = (event: FormEvent) => {
    const form = event.currentTarget as HTMLFormElement;
    event.preventDefault();
    if (form.checkValidity() === false) {
      event.stopPropagation();
      setValidated(true);
      return;
    }
    if (steps.length === 0) {
      toast.error("Add at least one process step.");
      setValidated(true);
      return;
    }
    if (saveLoading) return;
    void onSave();
  };

  async function onSave() {
    setSaveLoading(true);
    try {
      const wfOps = steps.map((value, idx) => ({
        ...value,
        opterionid: Number(value.opterionid),
        cycletime: String(value.cycletime ?? "0"),
        setuptime: String(value.setuptime ?? "0"),
        active: true,
        sequencenumber: String(idx + 1),
      }));
      const parsedOrder = parseWorkflowOrderKey(formInputs.orderKey || "");
      const selectedPart = partsList.find((p) => p.value === formInputs.partid);
      const isPlatform = parsedOrder?.source === "platform";

      const request: Record<string, unknown> = {
        ...formInputs,
        template_name: (formInputs.workflowname || "").trim(),
        orderId: parsedOrder?.id,
        orderid: parsedOrder?.id,
        orderSource: parsedOrder?.source,
        order_source: parsedOrder?.source,
        active: true,
        status: (formInputs.status || "Draft").trim(),
        workflowoperations: wfOps,
        steps: wfOps.map((s, i) => ({
          processDefinitionId: s.opterionid,
          cycleTimeMinutes: parseMinutes(String(s.cycletime)),
          setupTimeMinutes: parseMinutes(String(s.setuptime)),
          sequenceNumber: i + 1,
        })),
      };

      if (isPlatform && selectedPart) {
        request.partid = null;
        request.partId = null;
        request.order_part_name = selectedPart.orderPartName ?? selectedPart.partname;
        request.orderPartName = selectedPart.orderPartName ?? selectedPart.partname;
        request.order_line_id = selectedPart.orderLineId ?? null;
        request.orderLineId = selectedPart.orderLineId ?? null;
      } else if (selectedPart?.partid) {
        request.partid = selectedPart.partid;
        request.partId = selectedPart.partid;
        request.order_part_name = null;
        request.orderPartName = null;
        request.order_line_id = null;
        request.orderLineId = null;
      }
      if (editId) {
        request.id = editId;
      }
      let saveList;
      if (editId) {
        saveList = await updateById(ENDPOINT.WORKFLOWS_UPDATE, request);
      } else {
        saveList = await addList(ENDPOINT.WORKFLOWS_ADD, request);
      }
      if (saveList === "success") {
        toast.success(
          editId
            ? "Workflow template updated and assigned to the order part."
            : "Workflow template created and assigned to the order part.",
        );
        await onLoad(pageIndex, searchText);
        handleClose();
      } else {
        toast.error("Could not save workflow template.");
      }
    } catch (e) {
      console.log(`Error in save workflow: ${e}`);
      toast.error("Could not save workflow template.");
    } finally {
      setSaveLoading(false);
    }
  }

  async function onEdit(id: number) {
    try {
      await loadModalLists();
      const raw = await getListById(ENDPOINT.WORKFLOWS_LIST_BY_ID, id);
      const wfRaw = Array.isArray(raw) ? raw[0] : raw;
      const wf = normalizeWorkflowPayload(wfRaw);
      if (!wf) {
        toast.error("Workflow not found.");
        return;
      }
      setEditId(wf.id);
      const orderSource = (wf.order_source ?? wf.orderSource ?? "manual") as OrderSource;
      const orderIdStr = wf.orderid != null ? String(wf.orderid) : "";
      const orderKey =
        orderIdStr && Number.isFinite(Number(orderIdStr))
          ? buildWorkflowOrderKey(orderSource, Number(orderIdStr))
          : "";
      setForm({
        workflowname: wf.workflowname ?? "",
        orderKey,
        partid: "",
        status: wf.status && String(wf.status).trim() ? String(wf.status) : "Draft",
      });
      if (orderKey) {
        await loadPartsForOrder(orderKey, { wf });
      } else {
        setParts([]);
      }
      const loaded = (wf.workflowoperations ?? [])
        .filter((o) => o.active !== false)
        .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
        .map((o) => ({
          id: o.id,
          opterionid: o.opterionid != null ? String(o.opterionid) : "",
          cycletime: String(parseMinutes(o.cycletime)),
          setuptime: String(parseMinutes(o.setuptime)),
        }));
      setSteps(loaded);
      setDraftOpId("");
      setDraftCycle(10);
      setDraftSetup(5);
      setAddUpdateModal(true);
    } catch (e) {
      console.log(`Error in edit workflow: ${e}`);
      toast.error("Could not load workflow.");
    }
  }

  async function onDelete(list: WorkflowRow) {
    const isConfirmed = await confirm();
    if (!isConfirmed) return;

    try {
      const payload = {
        id: list.id,
        workflowname: list.workflowname,
        partid: list.partid != null ? Number(list.partid) : undefined,
        active: false,
      };
      const saveWorkflows = await updateById(ENDPOINT.WORKFLOWS_UPDATE, payload);
      if (saveWorkflows === "success") {
        await onLoad(pageIndex, searchText);
      } else {
        toast.error("Could not delete workflow.");
      }
    } catch (e) {
      console.log(`Error in delete: ${e}`);
      toast.error("Could not delete workflow.");
    }
  }

  function addStepFromDraft() {
    if (!draftOpId) return;
    const c = Math.max(0, Number.isFinite(draftCycle) ? draftCycle : 0);
    const s = Math.max(0, Number.isFinite(draftSetup) ? draftSetup : 0);
    setSteps((prev) => [
      ...prev,
      {
        opterionid: draftOpId,
        cycletime: String(c),
        setuptime: String(s),
      },
    ]);
    setDraftOpId("");
    setDraftCycle(10);
    setDraftSetup(5);
  }

  function removeStep(index: number) {
    setSteps((prev) => prev.filter((_, i) => i !== index));
  }

  function onDraftMinutesNumberChange(
    setter: React.Dispatch<React.SetStateAction<number>>,
    raw: string,
  ) {
    if (raw === "") {
      setter(0);
      return;
    }
    const n = parseInt(raw, 10);
    if (!Number.isNaN(n)) {
      setter(Math.max(0, Math.min(n, 999_999)));
    }
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setForm((values) => ({ ...values, [name]: value }));
  };

  const handleOrderChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const orderKey = event.target.value;
    setForm((values) => ({ ...values, orderKey, partid: "" }));
    if (!orderKey) {
      setParts([]);
      return;
    }
    void loadPartsForOrder(orderKey);
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const showPagination = totalPages > 1;
  const hasRecords = workflowsCollection?.length > 0;

  const modalTotals = useMemo(() => {
    let c = 0;
    let s = 0;
    for (const row of steps) {
      c += parseMinutes(row.cycletime);
      s += parseMinutes(row.setuptime);
    }
    return { cycle: c, setup: s };
  }, [steps]);

  const saveDisabled =
    !(formInputs.workflowname ?? "").trim() ||
    !formInputs.orderKey ||
    !formInputs.partid ||
    steps.length === 0;

  return (
    <div className="wf-templates-page">
      {isLoading && <AppLoader text="Loading workflow templates..." />}
      <div className="wf-templates">
          <div className="wf-templates__shell">
            <div className="wf-templates__head">
              <div>
                <p className="wf-templates__subtitle">
                  Reusable step sequences built from Process Definitions. Feed into
                  Recipes.
                </p>
                <nav className="wf-templates__crumbs" aria-label="Planning hierarchy">
                  <Link className="wf-templates__crumb" to="/mes-operations">
                    <i className="fa fa-cog" aria-hidden />
                    Process Definitions
                  </Link>
                  <span className="wf-templates__crumb-sep" aria-hidden>
                    &gt;
                  </span>
                  <span className="wf-templates__crumb wf-templates__crumb--active">
                    <i className="fa fa-sitemap" aria-hidden />
                    Workflow Templates
                  </span>
                  <span className="wf-templates__crumb-sep" aria-hidden>
                    &gt;
                  </span>
                  <Link className="wf-templates__crumb" to="/mes-trurecipe/library">
                    <i className="fa fa-flask" aria-hidden />
                    Recipes
                  </Link>
                  <span className="wf-templates__crumb-sep" aria-hidden>
                    &gt;
                  </span>
                  <Link className="wf-templates__crumb" to="/mes-jobs">
                    <i className="fa fa-wrench" aria-hidden />
                    Jobs
                  </Link>
                </nav>
              </div>
              <button
                type="button"
                className="wf-templates__add-btn"
                onClick={handleShowCreate}
              >
                <i className="fa fa-plus" aria-hidden />
                Add Template
              </button>
            </div>

            <div className="wf-templates__search-wrap">
              <i className="fa fa-search wf-templates__search-icon" aria-hidden />
              <input
                type="search"
                placeholder="Search Templates"
                className="wf-templates__search-input"
                value={searchText}
                onChange={(e) => {
                  const v = e.target.value;
                  setSearchText(v);
                  scheduleSearchReload(v);
                }}
                aria-label="Search templates"
              />
            </div>

            {!hasRecords ? (
              <MesEmptyState
                icon="fa-random"
                title="No workflow templates"
                subtitle="Create a template to define reusable step sequences from process definitions."
              />
            ) : (
              <>
                <div className="wf-templates__list">
                  {workflowsCollection.map((list) => {
                    const { steps: nSteps, minutes } = workflowListMeta(list);
                    const partLabel = list.part_name ?? list.partDisplayName ?? "—";
                    const orderLabel = list.order_code ?? list.orderCode ?? "—";
                    const title =
                      (list.template_name ?? list.workflowname ?? "").trim() || "—";
                    const st = (list.status ?? "Draft").toLowerCase() === "approved"
                      ? "approved"
                      : "draft";
                    const recipes = Number(list.recipe_count ?? list.recipeCount ?? 0);
                    const expanded = expandedWorkflowId === list.id;
                    const detail = workflowDetails[list.id];
                    const detailOps = (detail?.workflowoperations ?? [])
                      .filter((o) => o.active !== false)
                      .sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
                    const displayOps = buildDisplayWorkflowOps(detailOps as WoRow[]);

                    return (
                      <article
                        key={list.id}
                        className={`wf-templates__card${expanded ? " wf-templates__card--open" : ""}`}
                        aria-expanded={expanded}
                      >
                        <div
                          className="wf-templates__card-row"
                          onClick={() =>
                            setExpandedWorkflowId((prev) => (prev === list.id ? null : list.id))
                          }
                        >
                          <div className="wf-templates__card-main">
                            <h2 className="wf-templates__card-name">{title}</h2>
                            <p className="wf-templates__card-meta">
                              Order: {orderLabel} · Part: {partLabel} · {nSteps} step
                              {nSteps === 1 ? "" : "s"} · {minutes} min total
                            </p>
                          </div>
                          <div className="wf-templates__card-aside">
                            {recipes > 0 && (
                              <span className="wf-templates__recipe-badge">
                                <i className="fa fa-flask" aria-hidden />
                                {recipes} recipe{recipes === 1 ? "" : "s"}
                              </span>
                            )}
                            <div onClick={(e) => e.stopPropagation()}>
                              <span
                                className={`wf-templates__status-badge wf-templates__status-badge--${st}`}
                                role="status"
                              >
                                {st === "approved" ? "Approved" : "Draft"}
                              </span>
                            </div>
                            <div
                              className="wf-templates__actions"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                type="button"
                                className="wf-templates__icon-btn"
                                title="Edit"
                                onClick={() => void onEdit(list.id)}
                              >
                                <img src={WriteLogo} alt="" />
                              </button>
                              <button
                                type="button"
                                className="wf-templates__icon-btn wf-templates__icon-btn--danger"
                                title="Delete"
                                onClick={() => void onDelete(list)}
                              >
                                <img src={DeleteLogo} alt="" />
                              </button>
                            </div>
                            <span
                              className="wf-templates__chevron"
                              aria-hidden
                            >
                              <i
                                className={`fa fa-chevron-${expanded ? "up" : "down"}`}
                              />
                            </span>
                          </div>
                        </div>

                        {expanded && (
                          <div className="wf-templates__expand">
                            {!detail ? (
                              <p className="wf-templates__expand-loading">
                                Loading details…
                              </p>
                            ) : (
                              <>
                                {displayOps.length > 0 && (
                                  <div
                                    className="wf-templates__flow-strip"
                                    aria-label="Step sequence"
                                  >
                                    {displayOps.map((op, idx) => {
                                      const label =
                                        (op as WoRow).processDefinitionName ??
                                        opNameById[String(op.opterionid ?? "")] ??
                                        "—";
                                      const last = idx === displayOps.length - 1;
                                      return (
                                        <Fragment key={op.id ?? `flow-${idx}`}>
                                          <span
                                            className="wf-templates__flow-pill"
                                            title={label}
                                          >
                                            {label}
                                          </span>
                                          {!last && (
                                            <span
                                              className="wf-templates__flow-chevron"
                                              aria-hidden
                                            >
                                              <i className="fa fa-angle-right" />
                                            </span>
                                          )}
                                        </Fragment>
                                      );
                                    })}
                                  </div>
                                )}
                                <div className="wf-templates__detail-table-wrap">
                                  <table className="wf-templates__detail-table">
                                    <thead>
                                      <tr>
                                        <th>#</th>
                                        <th>Process definition</th>
                                        <th>Machine</th>
                                        <th>Mfg. technology</th>
                                        <th>Cycle</th>
                                        <th>Setup</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {displayOps.map((op, idx) => {
                                        const oid = String(op.opterionid ?? "");
                                        const proc =
                                          (op as WoRow).processDefinitionName ??
                                          opNameById[oid] ??
                                          "—";
                                        const machine =
                                          (op as WoRow).machineName ?? "—";
                                        const tech =
                                          (op as WoRow).mfgTechnologyName ?? "—";
                                        return (
                                          <tr key={op.id ?? `row-${idx}`}>
                                            <td>{idx + 1}</td>
                                            <td className="wf-templates__proc-cell">{proc}</td>
                                            <td>{machine}</td>
                                            <td>
                                              {tech && tech !== "—" ? (
                                                <Link
                                                  className="wf-templates__tech-link"
                                                  to="/mes-operations"
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  {tech}
                                                </Link>
                                              ) : (
                                                "—"
                                              )}
                                            </td>
                                            <td>{parseMinutes(op.cycletime)} min</td>
                                            <td>{parseMinutes(op.setuptime)} min</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                                <div className="wf-templates__expand-foot">
                                  {recipes > 0 ? (
                                    <Link
                                      className="wf-templates__expand-link"
                                      to="/mes-trurecipe/library"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {recipes} recipe{recipes === 1 ? "" : "s"} linked to
                                      this workflow →{" "}
                                      <span className="wf-templates__expand-link-accent">
                                        View in Library
                                      </span>
                                    </Link>
                                  ) : (
                                    <span className="wf-templates__expand-muted">
                                      No recipes linked yet.
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    className="wf-templates__create-recipe"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setRecipePanelWfId((prev) =>
                                        prev === list.id ? null : list.id,
                                      );
                                    }}
                                  >
                                    + Create Recipe
                                  </button>
                                </div>
                                {recipePanelWfId === list.id && (
                                  <div
                                    className="wf-templates__recipe"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="wf-templates__recipe-head">
                                      <span className="wf-templates__recipe-icon" aria-hidden>
                                        <i className="fa fa-file-text-o" />
                                      </span>
                                      <div>
                                        <h3 className="wf-templates__recipe-title">
                                          Create Recipe for This Workflow
                                        </h3>
                                        <p className="wf-templates__recipe-sub">
                                          Manufacturing technology defines the parameter template
                                          for each step. Fill in target, nominal, and tolerance values.
                                        </p>
                                      </div>
                                    </div>
                                    <Row className="g-3 mb-2">
                                      <Col md={12}>
                                        <Form.Label className="wf-templates__recipe-label">
                                          Recipe name
                                        </Form.Label>
                                        <Form.Control
                                          type="text"
                                          className="wf-templates__recipe-input"
                                          value={recipeName}
                                          onChange={(e) => setRecipeName(e.target.value)}
                                          isInvalid={recipeValidated && !recipeName.trim()}
                                          required
                                        />
                                        <Form.Control.Feedback type="invalid">
                                          Enter a recipe name.
                                        </Form.Control.Feedback>
                                      </Col>
                                      <Col md={12}>
                                        <Form.Label className="wf-templates__recipe-label">
                                          Material category
                                        </Form.Label>
                                        <Form.Select
                                          className="form-control custom-select wf-templates__recipe-select"
                                          value={recipeCategoryId}
                                          onChange={(e) => {
                                            setRecipeCategoryId(e.target.value);
                                            setRecipeGradeIds([]);
                                          }}
                                          isInvalid={
                                            recipeValidated &&
                                            (!recipeCategoryId ||
                                              !Number.isFinite(Number(recipeCategoryId)) ||
                                              Number(recipeCategoryId) <= 0)
                                          }
                                        >
                                          <option value="">Select category</option>
                                          {materialCategories.map((c) => (
                                            <option key={c.id} value={String(c.id)}>
                                              {(c.name ?? "").trim() || `Category #${c.id}`}
                                            </option>
                                          ))}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">
                                          Select a material category.
                                        </Form.Control.Feedback>
                                      </Col>
                                    </Row>
                                    <Row className="g-3 mb-2">
                                      <Col md={12}>
                                        <Form.Label className="wf-templates__recipe-label">
                                          Material grades
                                        </Form.Label>
                                        <RecipeGradeMultiSelect
                                          disabled={!recipeCategoryId}
                                          grades={materialGrades}
                                          value={recipeGradeIds}
                                          onChange={setRecipeGradeIds}
                                          invalid={
                                            recipeValidated && recipeGradeIds.length === 0
                                          }
                                          placeholder={
                                            recipeCategoryId
                                              ? "Search and select one or more grades…"
                                              : "Select category first"
                                          }
                                        />
                                        {recipeValidated && recipeGradeIds.length === 0 ? (
                                          <div className="wf-templates__recipe-field-error">
                                            Select at least one material grade.
                                          </div>
                                        ) : null}
                                      </Col>
                                    </Row>
                                    {displayOps.map((op, sIdx) => {
                                      const proc =
                                        op.processDefinitionName ??
                                        opNameById[String(op.opterionid ?? "")] ??
                                        "—";
                                      const tech = (op.mfgTechnologyName ?? "—").trim() || "—";
                                      const stepGroups =
                                        recipePanelWfId != null
                                          ? stepParamGroupsByWf[recipePanelWfId] ?? null
                                          : null;
                                      const groups = resolveRecipeGroupsForStep(
                                        stepGroups,
                                        sIdx,
                                        op,
                                      );
                                      return (
                                        <div
                                          key={op.id ?? `recipe-step-${sIdx}`}
                                          className="wf-templates__recipe-step"
                                        >
                                          <div className="wf-templates__recipe-step-head">
                                            <span className="wf-templates__recipe-step-num">
                                              {sIdx + 1}
                                            </span>
                                            <strong className="wf-templates__recipe-step-title">
                                              {proc}
                                            </strong>
                                            {tech !== "—" && (
                                              <span className="wf-templates__recipe-tech">
                                                {tech}
                                              </span>
                                            )}
                                          </div>
                                          {groups.map((g) => (
                                            <div key={g.title} className="wf-templates__recipe-block">
                                              <div className="wf-templates__recipe-group-title">
                                                {g.title}
                                              </div>
                                              <Row className="g-2">
                                                {g.params.map((p) => {
                                                  const cell = (slot: "t" | "n" | "x") =>
                                                    `${sIdx}-${p.key}-${slot}`;
                                                  const targetInvalid =
                                                    recipeValidated &&
                                                    recipeValidationTier === "full" &&
                                                    Boolean(p.required) &&
                                                    !(recipeCells[cell("t")] ?? "").trim();
                                                  return (
                                                    <Col md={6} key={p.key}>
                                                      <div
                                                        className={`wf-templates__recipe-param${
                                                          targetInvalid
                                                            ? " wf-templates__recipe-param--invalid"
                                                            : ""
                                                        }`}
                                                      >
                                                        <span
                                                          className={
                                                            p.required
                                                              ? "wf-templates__recipe-param-label wf-templates__recipe-param-label--req"
                                                              : "wf-templates__recipe-param-label"
                                                          }
                                                        >
                                                          {p.required ? "* " : ""}
                                                          {p.label}
                                                        </span>
                                                        <div className="wf-templates__recipe-tmm">
                                                          <div className="wf-templates__recipe-tmm-cell">
                                                            <Form.Control
                                                              type="text"
                                                              className="wf-templates__recipe-input wf-templates__recipe-input--sm"
                                                              placeholder="Target"
                                                              value={recipeCells[cell("t")] ?? ""}
                                                              isInvalid={targetInvalid}
                                                              onChange={(e) =>
                                                                setRecipeCells((prev) => ({
                                                                  ...prev,
                                                                  [cell("t")]: e.target.value,
                                                                }))
                                                              }
                                                            />
                                                            {p.unit ? (
                                                              <span className="wf-templates__recipe-unit">
                                                                {p.unit}
                                                              </span>
                                                            ) : null}
                                                          </div>
                                                          <Form.Control
                                                            type="text"
                                                            className="wf-templates__recipe-input wf-templates__recipe-input--sm"
                                                            placeholder="Nominal"
                                                            value={recipeCells[cell("n")] ?? ""}
                                                            onChange={(e) =>
                                                              setRecipeCells((prev) => ({
                                                                ...prev,
                                                                [cell("n")]: e.target.value,
                                                              }))
                                                            }
                                                          />
                                                          <Form.Control
                                                            type="text"
                                                            className="wf-templates__recipe-input wf-templates__recipe-input--sm"
                                                            placeholder="Tolerance"
                                                            value={recipeCells[cell("x")] ?? ""}
                                                            onChange={(e) =>
                                                              setRecipeCells((prev) => ({
                                                                ...prev,
                                                                [cell("x")]: e.target.value,
                                                              }))
                                                            }
                                                          />
                                                        </div>
                                                      </div>
                                                    </Col>
                                                  );
                                                })}
                                              </Row>
                                            </div>
                                          ))}
                                        </div>
                                      );
                                    })}
                                    <div className="wf-templates__recipe-foot">
                                      <Button
                                        type="button"
                                        variant="outline-secondary"
                                        className="wf-templates__recipe-save me-2"
                                        disabled={recipeSaveBusy}
                                        onClick={() => void saveWorkflowRecipe(list.id, "draft")}
                                      >
                                        {recipeSaveBusy ? (
                                          <Spinner animation="border" size="sm" className="me-1" />
                                        ) : (
                                          <i className="fa fa-save me-1" aria-hidden />
                                        )}
                                        Save draft
                                      </Button>
                                      <Button
                                        type="button"
                                        variant="success"
                                        className="wf-templates__recipe-save me-2"
                                        disabled={recipeSaveBusy}
                                        onClick={() => void saveWorkflowRecipe(list.id, "approved")}
                                      >
                                        {recipeSaveBusy ? (
                                          <Spinner animation="border" size="sm" className="me-1" />
                                        ) : (
                                          <i className="fa fa-check me-1" aria-hidden />
                                        )}
                                        {recipeSavedId ? "Save & approve" : "Save Recipe"}
                                      </Button>
                                      <button
                                        type="button"
                                        className="wf-templates__recipe-cancel"
                                        disabled={recipeSaveBusy}
                                        onClick={() => setRecipePanelWfId(null)}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </article>
                    );
                  })}
                </div>

                {showPagination && (
                  <div className="wf-templates__pagination">
                    <nav aria-label="Workflow templates pagination">
                      <ul className="pagination">
                        <li className={`page-item ${pageIndex === 0 ? "disabled" : ""}`}>
                          <button
                            type="button"
                            className="page-link"
                            disabled={pageIndex === 0}
                            onClick={() => void onLoad(pageIndex - 1)}
                          >
                            &laquo;
                          </button>
                        </li>
                        {Array.from({ length: totalPages }).map((_, i) => (
                          <li
                            key={i}
                            className={`page-item ${i === pageIndex ? "active" : ""}`}
                          >
                            <button
                              type="button"
                              className="page-link"
                              onClick={() => void onLoad(i)}
                            >
                              {i + 1}
                            </button>
                          </li>
                        ))}
                        <li
                          className={`page-item ${
                            pageIndex >= totalPages - 1 ? "disabled" : ""
                          }`}
                        >
                          <button
                            type="button"
                            className="page-link"
                            disabled={pageIndex >= totalPages - 1}
                            onClick={() => void onLoad(pageIndex + 1)}
                          >
                            &raquo;
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

      <Modal
        className={`wf-templates-modal${saveLoading ? " wf-templates-modal--saving" : ""}`}
        size="lg"
        centered
        show={openAddUpdateModal}
        onHide={handleClose}
        backdrop="static"
      >
        <Modal.Header closeButton={false} className="wf-templates-modal__header">
          <div>
            <Modal.Title as="h2" className="wf-templates-modal__title">
              {editId ? "Edit Workflow Template" : "Create Workflow Template"}
            </Modal.Title>
            <p className="wf-templates-modal__subtitle">
              Define a reusable step sequence built from Process Definitions.
            </p>
          </div>
          <button
            type="button"
            className="wf-templates-modal__close"
            aria-label="Close"
            disabled={saveLoading}
            onClick={handleClose}
          >
            <i className="fa fa-times" aria-hidden />
          </button>
        </Modal.Header>
        <Modal.Body className="wf-templates-modal__body">
          {saveLoading && (
            <div className="wf-templates-modal__saving-overlay" aria-busy="true">
              <Spinner animation="border" role="status" size="sm" />
              <span>Saving template…</span>
            </div>
          )}
          <Form
            noValidate
            validated={validated}
            onSubmit={handleSubmit}
            id="wf-templates-form"
          >
            <Row className="g-3 mb-1">
              <Col md={12}>
                <Form.Group controlId="workflowname">
                  <Form.Label>Template Name</Form.Label>
                  <Form.Control
                    required
                    type="text"
                    name="workflowname"
                    className="wf-templates-modal__text-input"
                    placeholder="e.g. Crankshaft Housing — Standard"
                    value={formInputs.workflowname || ""}
                    onChange={handleChange}
                  />
                  <Form.Control.Feedback type="invalid">
                    Template name is required.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mb-2">
              <Col md={4}>
                <Form.Group controlId="orderKey">
                  <Form.Label>Order</Form.Label>
                  <Form.Select
                    required
                    name="orderKey"
                    className="form-control custom-select wf-templates-modal__select"
                    value={formInputs.orderKey || ""}
                    onChange={handleOrderChange}
                  >
                    <option value="">Select order</option>
                    {ordersList.map((o) => (
                      <option key={o.orderKey} value={o.orderKey}>
                        {o.ordercode}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    Select an order.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="partid">
                  <Form.Label>Part</Form.Label>
                  <Form.Select
                    required
                    name="partid"
                    className="form-control custom-select wf-templates-modal__select"
                    value={formInputs.partid || ""}
                    onChange={handleChange}
                    disabled={!formInputs.orderKey || orderPartsLoading}
                  >
                    <option value="">
                      {orderPartsLoading
                        ? "Loading parts…"
                        : formInputs.orderKey
                          ? "Select part"
                          : "Select order first"}
                    </option>
                    {partsList?.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.partname}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    Select a part.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="wf-template-status">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="status"
                    className="form-control custom-select wf-templates-modal__select"
                    value={formInputs.status || "Draft"}
                    onChange={handleChange}
                  >
                    <option value="Draft">Draft</option>
                    <option value="Approved">Approved</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <div className="wf-templates-modal__section-label">
              Steps (from process definitions)
            </div>

            <div className="wf-templates-modal__add-step">
              <Row className="g-2 align-items-end wf-templates-modal__add-step-row">
                <Col md={5} sm={12}>
                  <Form.Label className="small text-muted mb-1">
                    Process definition
                  </Form.Label>
                  <Form.Select
                    value={draftOpId}
                    onChange={(e) => setDraftOpId(e.target.value)}
                    aria-label="Process definition"
                    className="form-control custom-select wf-templates-modal__select"
                  >
                    <option value="">Select process…</option>
                    {operationList.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.operationname ?? `Operation #${o.id}`}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={2} sm={6}>
                  <Form.Label className="small text-muted mb-1">Cycle (min)</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    step={1}
                    autoComplete="off"
                    className="wf-templates-modal__text-input wf-templates-modal__text-input--number"
                    value={draftCycle}
                    onChange={(e) => onDraftMinutesNumberChange(setDraftCycle, e.target.value)}
                  />
                </Col>
                <Col md={2} sm={6}>
                  <Form.Label className="small text-muted mb-1">Setup (min)</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    step={1}
                    autoComplete="off"
                    className="wf-templates-modal__text-input wf-templates-modal__text-input--number"
                    value={draftSetup}
                    onChange={(e) => onDraftMinutesNumberChange(setDraftSetup, e.target.value)}
                  />
                </Col>
                <Col md={3} sm={12} className="text-md-end">
                  <Form.Label className="small text-muted mb-1 d-none d-md-block">
                    &nbsp;
                  </Form.Label>
                  <button
                    type="button"
                    className="wf-templates-modal__add-btn wf-templates-modal__add-btn--block-sm"
                    onClick={addStepFromDraft}
                    disabled={!draftOpId}
                  >
                    <i className="fa fa-plus" aria-hidden /> Add
                  </button>
                </Col>
              </Row>
            </div>

            {steps.length > 0 && (
              <div className="wf-templates-modal__steps-table-wrap wf-templates-modal__steps-table-wrap--after-add">
                <table className="wf-templates-modal__steps-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Process definition</th>
                      <th>Machine</th>
                      <th>Mfg. technology</th>
                      <th>Cycle (min)</th>
                      <th>Setup (min)</th>
                      <th aria-label="Remove" />
                    </tr>
                  </thead>
                  <tbody>
                    {steps.map((row, idx) => {
                      const meta = opMetaById[row.opterionid] ?? {
                        machine: "—",
                        tech: "—",
                        techId: null,
                      };
                      return (
                        <tr key={row.id ?? `new-${idx}`}>
                          <td>{idx + 1}</td>
                          <td>{opNameById[row.opterionid] ?? "—"}</td>
                          <td>{meta.machine}</td>
                          <td>
                            {meta.tech && meta.tech !== "—" ? (
                              <Link
                                className="wf-templates-modal__tech-link"
                                to="/mes-operations"
                              >
                                {meta.tech}
                              </Link>
                            ) : (
                              <span className="wf-templates-modal__tech-muted">—</span>
                            )}
                          </td>
                          <td>{parseMinutes(row.cycletime)}</td>
                          <td>{parseMinutes(row.setuptime)}</td>
                          <td>
                            <button
                              type="button"
                              className="wf-templates__icon-btn wf-templates__icon-btn--danger"
                              title="Remove step"
                              onClick={() => removeStep(idx)}
                            >
                              <i className="fa fa-trash-o" aria-hidden />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={4}>Total</td>
                      <td>{modalTotals.cycle}</td>
                      <td>{modalTotals.setup}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {steps.length === 0 && (
              <p className="wf-templates-modal__empty-steps">
                No steps added yet. Use the form above to add process steps.
              </p>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer className="wf-templates-modal__footer">
          <Button
            type="button"
            variant="light"
            className="wf-templates-modal__btn-cancel"
            disabled={saveLoading}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="wf-templates-form"
            className="wf-templates-modal__btn-save"
            disabled={saveDisabled || saveLoading}
          >
            {saveLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" role="status" />
                Saving…
              </>
            ) : (
              "Save Template"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      <ConfirmDialog />
    </div>
  );
};

export default MesWorkflows;
